import STOP_WORDS from './stopwords';

export const isUseful = (w) => w.length >= 4 && !STOP_WORDS.has(w) && !/^\d+$/.test(w);

export const tokenize = (text) =>
  text.toLowerCase().replace(/[^a-z0-9\s-]/g, '').split(/\s+/).filter(w => w.length > 0);

export const POWER_WORDS = new Set([
  'secret','secrets','mistake','mistakes','truth','never','best','worst','hack','hacks',
  'hidden','ultimate','proven','shocking','insane','incredible','amazing','powerful',
  'essential','critical','dangerous','warning','urgent','breaking','exclusive','rare',
  'banned','illegal','controversial','exposed','revealed','unknown','underrated',
  'overrated','perfect','brilliant','genius','epic','legendary','massive','tiny',
  'cheap','expensive','free','fastest','slowest','easiest','hardest','strangest',
  'weirdest','craziest','deadliest','richest','poorest','biggest','smallest',
  'transform','destroy','dominate','eliminate','explode','crush','master','unlock',
  'discover','avoid','survive','escape','rescue','upgrade','downgrade','revolution',
  'breakthrough','nightmare','disaster','crisis','scam','fraud','trap','lie','lies',
  'myth','myths','real','fake','honest','brutal','raw','unfiltered',
]);

export const splitSentences = (text) =>
  text.replace(/\[.*?\]/g, '').split(/[.!?]+/).map(s => s.trim()).filter(s => s.length >= 10);

export const extractQuestions = (text) => {
  const sentences = text.replace(/\[.*?\]/g, '').split(/[.!?]+/).map(s => s.trim());
  const questionStarters = /^(how|what|why|when|where|who|which|can|could|should|would|is|are|do|does|will|have|has|did)/i;
  return sentences
    .filter(s => s.length >= 15 && questionStarters.test(s))
    .slice(0, 10);
};

export const extractQuotables = (text, topWordSet) => {
  const sentences = splitSentences(text);
  return sentences
    .filter(s => {
      const words = tokenize(s);
      return words.length >= 3 && words.length <= 12;
    })
    .map(s => {
      const words = tokenize(s);
      const hits = words.filter(w => topWordSet.has(w)).length;
      const density = words.length > 0 ? hits / words.length : 0;
      return { sentence: s, density, hits };
    })
    .filter(s => s.hits >= 2)
    .sort((a, b) => b.density - a.density)
    .slice(0, 8)
    .map(s => s.sentence);
};

export const buildTimeline = (text, topWords) => {
  const allWords = tokenize(text);
  const totalWords = allWords.length;
  if (totalWords < 20) return [];

  const quarters = ['Intro (0-25%)', 'Early (25-50%)', 'Mid (50-75%)', 'End (75-100%)'];
  const quarterSize = Math.floor(totalWords / 4);

  return topWords.slice(0, 8).map(keyword => {
    const counts = [0, 0, 0, 0];
    allWords.forEach((w, i) => {
      if (w === keyword) {
        const q = Math.min(Math.floor(i / quarterSize), 3);
        counts[q]++;
      }
    });
    return { keyword, counts, labels: quarters };
  });
};

export const findPowerWords = (text) => {
  const words = tokenize(text);
  const found = {};
  for (const w of words) {
    if (POWER_WORDS.has(w)) {
      found[w] = (found[w] || 0) + 1;
    }
  }
  return Object.entries(found)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count);
};

export const generateTitles = (singles, phrases, powerWords) => {
  const titles = [];
  const top = singles.slice(0, 5).map(s => s.word);
  const topPhrases = phrases.slice(0, 3).map(p => p.word);
  const topPower = powerWords.slice(0, 3).map(p => p.word);
  const capitalize = (s) => s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  if (topPhrases[0]) {
    titles.push(`How to ${capitalize(topPhrases[0])} (Complete Guide)`);
    titles.push(`${capitalize(topPhrases[0])} - Everything You Need to Know`);
  }
  if (top[0] && top[1]) {
    titles.push(`${capitalize(top[0])} vs ${capitalize(top[1])} - Which Is Better?`);
    titles.push(`Top 10 ${capitalize(top[0])} Tips for ${capitalize(top[1])}`);
  }
  if (top[0]) {
    titles.push(`Why ${capitalize(top[0])} Changes Everything`);
    titles.push(`The Truth About ${capitalize(top[0])}`);
    titles.push(`${capitalize(top[0])} Explained in Simple Terms`);
  }
  if (topPower[0] && top[0]) {
    titles.push(`The Most ${capitalize(topPower[0])} ${capitalize(top[0])} You'll Ever See`);
  }
  if (topPhrases[1]) {
    titles.push(`Why ${capitalize(topPhrases[1])} Matters More Than You Think`);
  }
  if (top[0] && top[2]) {
    titles.push(`${capitalize(top[0])}: ${capitalize(top[2])} Tips That Actually Work`);
  }

  return titles;
};

export const countSyllables = (word) => {
  const w = word.toLowerCase().replace(/[^a-z]/g, '');
  if (w.length <= 3) return 1;
  let count = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '').match(/[aeiouy]{1,2}/g);
  return count ? count.length : 1;
};

export const fleschKincaid = (text) => {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const words = tokenize(text);
  if (sentences.length === 0 || words.length === 0) return { grade: 0, ease: 0 };
  const totalSyllables = words.reduce((sum, w) => sum + countSyllables(w), 0);
  const avgWordsPerSentence = words.length / sentences.length;
  const avgSyllablesPerWord = totalSyllables / words.length;
  const ease = 206.835 - 1.015 * avgWordsPerSentence - 84.6 * avgSyllablesPerWord;
  const grade = 0.39 * avgWordsPerSentence + 11.8 * avgSyllablesPerWord - 15.59;
  return { grade: Math.max(0, Math.round(grade * 10) / 10), ease: Math.max(0, Math.min(100, Math.round(ease * 10) / 10)) };
};

export const analyzeKeywords = (text) => {
  const allWords = tokenize(text);
  const usefulWords = allWords.filter(isUseful);

  const wordCounts = {};
  for (const w of usefulWords) {
    wordCounts[w] = (wordCounts[w] || 0) + 1;
  }
  const singles = Object.entries(wordCounts)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 20);

  const phraseCounts = {};
  for (let i = 0; i < allWords.length - 1; i++) {
    const a = allWords[i], b = allWords[i + 1];
    if (isUseful(a) && isUseful(b)) {
      const phrase = `${a} ${b}`;
      phraseCounts[phrase] = (phraseCounts[phrase] || 0) + 1;
    }
  }
  const phrases = Object.entries(phraseCounts)
    .filter(([, count]) => count >= 2)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 15);

  const topWordSet = new Set(singles.slice(0, 10).map(s => s.word));
  const sentences = splitSentences(text).filter(s => s.length >= 20);

  const scored = sentences.map(sentence => {
    const words = tokenize(sentence);
    const hits = words.filter(w => topWordSet.has(w)).length;
    const score = words.length > 0 ? hits / words.length : 0;
    return { sentence, score, hits };
  });

  const seen = new Set();
  const summary = scored
    .filter(s => s.hits >= 2)
    .sort((a, b) => b.score - a.score)
    .filter(s => {
      const key = s.sentence.toLowerCase().substring(0, 40);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 5)
    .map(s => s.sentence);

  const questions = extractQuestions(text);
  const quotables = extractQuotables(text, topWordSet);
  const timeline = buildTimeline(text, singles.map(s => s.word));
  const powerWords = findPowerWords(text);
  const generatedTitles = generateTitles(singles, phrases, powerWords);
  const readability = fleschKincaid(text);

  return { singles, phrases, summary, questions, quotables, timeline, powerWords, generatedTitles, readability };
};
